import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

type FormMultiSelectProps = {
    label: string;
    name: string;
    value?: any[];
    onChange?: (value: any[]) => void;
    options: any[];
    textField?: string;
    error?: string;
    placeholder?: string;
    disabled?: boolean;
    renderItem?: (item: any) => React.ReactNode;
};

const FormMultiSelect = ({ label, name, value = [], onChange, options, textField = 'name', error, placeholder, disabled, renderItem }: FormMultiSelectProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);

    const getLabel = (item: any): string => {
        if (typeof item === 'string') return item;
        return item?.[textField] || '';
    };

    const getId = (item: any): string => {
        if (typeof item === 'string') return item;
        return item?.id || getLabel(item);
    };

    const isSelected = (item: any) => {
        return value.some(v => getId(v) === getId(item));
    };

    const filtered = (options || []).filter(o =>
        o && getLabel(o).toLowerCase().includes(search.toLowerCase()) && !isSelected(o)
    );

    const handleSelect = (item: any) => {
        const next = [...value, item];
        onChange?.(next);
        setSearch('');
    };

    const handleRemove = (item: any) => {
        const next = value.filter(v => getId(v) !== getId(item));
        onChange?.(next);
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="mb-4 relative" ref={wrapperRef}>
            <label
                htmlFor={name}
                className="block text-sm font-medium mb-1.5"
                style={{ color: 'var(--text-secondary)' }}
            >
                {label}
            </label>

            {/* Selected items + input */}
            <div
                className="flex flex-wrap gap-1.5 p-2 rounded-lg border cursor-text min-h-[38px]"
                style={{
                    backgroundColor: 'var(--surface-0)',
                    borderColor: 'var(--border-default)',
                }}
                onClick={() => !disabled && setOpen(true)}
            >
                {value.map((item, i) => (
                    <span
                        key={getId(item)}
                        className="badge-accent inline-flex items-center gap-1 py-1"
                    >
                        {getLabel(item)}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleRemove(item); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                        >
                            <X size={10} />
                        </button>
                    </span>
                ))}
                <input
                    type="text"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder={value.length === 0 ? (placeholder || 'Search...') : ''}
                    disabled={disabled}
                    className="flex-1 min-w-[60px] text-sm outline-none"
                    style={{
                        background: 'transparent',
                        color: 'var(--text-primary)',
                        border: 'none',
                    }}
                />
            </div>

            {/* Dropdown */}
            {open && filtered.length > 0 && (
                <div
                    className="absolute z-50 mt-1 w-full max-h-48 overflow-auto rounded-lg border"
                    style={{
                        backgroundColor: 'var(--bg-card)',
                        borderColor: 'var(--border-default)',
                        boxShadow: 'var(--shadow-lg)',
                    }}
                >
                    {filtered.map((item, i) => (
                        <button
                            key={getId(item) + i}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm transition-colors"
                            style={{
                                color: 'var(--text-primary)',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            onClick={() => handleSelect(item)}
                        >
                            {renderItem ? renderItem(item) : getLabel(item)}
                        </button>
                    ))}
                </div>
            )}

            {error && (
                <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>
            )}
        </div>
    );
};

export default FormMultiSelect;
